import React, { ComponentProps, forwardRef, ReactElement, ReactNode, Ref } from 'react'

import { ReactComponent as CaretDownIcon } from '@app/assets/Small/Outline/CaretDown.svg'
import { bemJoin, classNames } from '@app/lib/bem'
import { DropdownMenu } from '@app/lib/inputs/DropdownMenu'
import {
  DropdownItem,
  DropdownMenuAlign,
  DropdownMenuSize
} from '@app/lib/inputs/DropdownMenu/types'
import { InputBase, InputBaseSize } from '@app/lib/layout/InputBase'
import { MergeProps } from '@app/lib/types'
import { useMobile } from '@app/services/useMobile'

import './Select.scss'

export type SelectOption<V> = {
  label: ReactNode
  value: V
  leading?: ReactNode
  comment?: ReactNode
  disabled?: boolean
}

export type SelectProps<V> = MergeProps<
  Omit<ComponentProps<'button'>, 'value' | 'onChange'>,
  {
    options: Array<SelectOption<V>>
    value?: V
    onChange: (value?: V) => void
    placeholder?: string
    allowEmpty?: boolean
    compare?: (a: V, b: V) => boolean
    triggerSize?: InputBaseSize
    error?: boolean
    align?: DropdownMenuAlign
    modal?: boolean
  }
>

const b = bemJoin('select')

const menuSizeByTriggerSize: Record<InputBaseSize, DropdownMenuSize> = {
  xs: 'sm',
  sm: 'sm',
  md: 'md',
  lg: 'md'
}

const SelectInner = <V,>(
  {
    options,
    value,
    onChange,
    placeholder = '---',
    allowEmpty,
    compare = (a: V, x: V) => a === x,
    triggerSize = 'md',
    error,
    align = 'left',
    modal,
    disabled,
    className,
    ...rest
  }: SelectProps<V>,
  ref: Ref<HTMLButtonElement>
) => {
  const { isMobile } = useMobile()
  const dropdownMenuSize = isMobile ? 'md' : menuSizeByTriggerSize[triggerSize]
  const selectedOption = options.find(
    (option) => value !== undefined && compare(option.value, value)
  )

  const items: DropdownItem[] = [
    ...(allowEmpty
      ? [
          {
            key: '__empty__',
            label: placeholder,
            selected: !selectedOption,
            onClick: () => onChange(undefined)
          }
        ]
      : []),
    ...options.map((option, index) => ({
      key: `option-${index}`,
      label: option.label,
      leading: option.leading,
      comment: option.comment,
      disabled: option.disabled,
      selected: option === selectedOption,
      onClick: () => onChange(option.value)
    }))
  ]

  const inputBoxTrigger = (
    <InputBase
      {...rest}
      as='button'
      ref={ref}
      type='button'
      size={triggerSize}
      error={error}
      disabled={disabled}
      trailingIcon={<CaretDownIcon className={b('caret')} />}
      className={classNames(b({ error: !!error }), className)}
    >
      <span className={b('value')}>{selectedOption ? selectedOption.label : placeholder}</span>
    </InputBase>
  )

  if (disabled || !items.length) {
    return inputBoxTrigger
  }

  return (
    <DropdownMenu
      items={items}
      trigger={inputBoxTrigger}
      size={dropdownMenuSize}
      align={align}
      modal={modal}
      matchTriggerWidth
    />
  )
}

export const Select = forwardRef(SelectInner) as <V>(
  props: SelectProps<V> & { ref?: Ref<HTMLButtonElement> }
) => ReactElement | null
